import { BaseApi } from '../apiBase';
import type { 
  FormPageData, 
  FormResponses, 
  FormSubmissionResult,
  Draft,
  PageValidationResponse 
} from '../../types/publicForm.types';

class EventFormSubmissionApi extends BaseApi {
  constructor() {
    super(); // Uses default /api/portal base URL
  }

  // Get form page data using event context
  async getFormPage(eventSlug: string, formSlug: string, pageIndex: number): Promise<FormPageData> {
    return this.get<FormPageData>(
      `/events/${eventSlug}/forms/${formSlug}/survey_pages/${pageIndex}`
    );
  }

  // Submit form with event context
  async submitForm(eventSlug: string, formSlug: string, data: any): Promise<FormSubmissionResult> {
    const { authToken, ...submissionData } = data;
    
    return this.post<FormSubmissionResult>(
      `/events/${eventSlug}/forms/${formSlug}/submissions`,
      {
        auth_token: authToken,
        ...submissionData
      }
    );
  }

  // Draft endpoints with event context
  async createDraft(eventSlug: string, formSlug: string): Promise<Draft> {
    return this.postSimpleJson<Draft>(
      `/events/${eventSlug}/forms/${formSlug}/drafts`,
      {}
    );
  }

  async getDraft(eventSlug: string, formSlug: string, authToken: string): Promise<Draft> {
    return this.getSimpleJson<Draft>(
      `/events/${eventSlug}/forms/${formSlug}/drafts/${authToken}`
    );
  }

  async updateDraft(eventSlug: string, formSlug: string, authToken: string, updates: any): Promise<Draft> {
    return this.putSimpleJson<Draft>(
      `/events/${eventSlug}/forms/${formSlug}/drafts/${authToken}`,
      updates
    );
  }

  // Validation endpoints with event context
  async validatePage(eventSlug: string, formSlug: string, pageIndex: number, responses: any): Promise<PageValidationResponse> {
    return this.postSimpleJson<PageValidationResponse>(
      `/events/${eventSlug}/forms/${formSlug}/validations/validate_page`,
      { pageIndex, responses }
    );
  }

  async validateField(eventSlug: string, formSlug: string, questionId: string, value: any): Promise<{ valid: boolean; error?: string }> {
    return this.postSimpleJson<{ valid: boolean; error?: string }>(
      `/events/${eventSlug}/forms/${formSlug}/validations/validate_field`,
      { questionId, value }
    );
  }

  // File upload with event context
  async getUploadUrl(eventSlug: string, formSlug: string, filename: string, contentType?: string): Promise<{
    url: string;
    fields: Record<string, string>;
    key: string;
    filename: string;
  }> {
    const path = `/events/${eventSlug}/forms/${formSlug}/file_uploads`;
    
    try {
      const result = await this.postSimpleJson(
        path,
        { filename, content_type: contentType },
        { transformResponseKeys: false }
      );
      return result;
    } catch (error) {
      console.error('EventFormSubmissionApi.getUploadUrl - error:', error);
      throw error;
    }
  }
}

export const eventFormSubmissionApi = new EventFormSubmissionApi();